import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { disable2fa, fetch2faStatus } from '../api';
import { errorMessage } from '../lib/api';
import { Badge } from './Badge';
import { Callout } from './Callout';
import { Spinner } from './Spinner';
import { LoadingState, ErrorState } from './States';
import { TwoFactorEnroll } from './TwoFactorEnroll';

const STATUS_KEY = ['2fa-status'];

/**
 * Two-factor (TOTP) enrollment / disable UI for the Settings page's Security
 * section. The enrollment flow itself lives in the shared TwoFactorEnroll
 * component (also used by the mandatory EnrollTwoFactorPage); here it stays put
 * after enrolling (no onEnrolled), and the forced-enrollment banner shows when
 * the deployment requires 2FA and it isn't enabled yet.
 */
export function TwoFactorSettings() {
  const statusQuery = useQuery({ queryKey: STATUS_KEY, queryFn: fetch2faStatus });

  if (statusQuery.isLoading) {
    return <LoadingState label="Loading security status…" />;
  }
  if (statusQuery.isError) {
    return (
      <ErrorState
        message="Could not load security status"
        onRetry={() => statusQuery.refetch()}
      />
    );
  }
  return statusQuery.data?.enabled ? (
    <TwoFactorEnabledCard />
  ) : (
    <TwoFactorEnroll required={statusQuery.data?.required ?? false} />
  );
}

function TwoFactorEnabledCard() {
  const queryClient = useQueryClient();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');

  const disable = useMutation({
    mutationFn: () => disable2fa({ password, code: code.trim() }),
    onSuccess: () => {
      setPassword('');
      setCode('');
      queryClient.invalidateQueries({ queryKey: STATUS_KEY });
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    disable.mutate();
  };

  return (
    <div className="card space-y-4 p-5">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-ink">
          Two-factor authentication
        </h3>
        <Badge tone="success" size="md" dot>
          Enabled
        </Badge>
      </div>
      <p className="text-sm text-ink-muted">
        Sign-in requires a code from your authenticator. To turn it off, confirm
        your password and a current code.
      </p>
      <form onSubmit={onSubmit} className="grid max-w-md gap-3">
        <div>
          <label className="label" htmlFor="pw">
            Password
          </label>
          <input
            id="pw"
            type="password"
            autoComplete="current-password"
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="label" htmlFor="dcode">
            Authentication or recovery code
          </label>
          <input
            id="dcode"
            name="otp"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            aria-label="One-time authentication or recovery code"
            className="input font-mono"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
          />
        </div>
        {disable.isError && (
          <Callout tone="danger">
            {errorMessage(disable.error, 'Could not disable two-factor')}
          </Callout>
        )}
        <button
          type="submit"
          className="btn-danger w-fit"
          disabled={disable.isPending}
        >
          {disable.isPending && <Spinner />}
          Disable two-factor
        </button>
      </form>
    </div>
  );
}
